import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Input, message, Modal, Select, Spin } from 'antd';
import React, { FC, useState, useEffect } from 'react';
import TapdFieldDelete from '@/assets/images/public/tapd-field-delete.svg';
import { getUserId } from '@/infrastructure/auth';
import { getProductId } from '@/infrastructure/product-info';
import { storageSet } from '@/infrastructure/storage-utils';
import { getInitFields } from '@/presentation/components/tapd';
import { RenderUserSelect } from '@/presentation/components/tapd-info/create-tapd-modal/user-select';
import {
  getCustomFields,
  getIssueTapdInfos,
  saveIssueTapdInfos,
} from '@/presentation/components/tapd/api';
import {
  CustomFieldsType,
  FieldsType,
  formatFieldsValue,
  getLocalTapd,
  formatCustomFields,
} from '@/presentation/components/tapd/create-tapd-modal';
import { RenderAutoComplete } from '@/presentation/components/tapd/create-tapd-modal/auto-complete';
import { RenderInput } from '@/presentation/components/tapd/create-tapd-modal/input';
import { RenderSelect } from '@/presentation/components/tapd/create-tapd-modal/select';
import { FilterOptions } from '@/proto/alert';
import { ExOption, FieldKey, getLocalField, Option } from '@/proto/field';
import { IBasicItemInfo, IStatusLinkageRule, ITapdTemplateInfos } from '@/proto/manage';

export type EditType = 'add' | 'update';

interface IProps {
  appVersionOptions: Option[] | ExOption[],
  visible: boolean;
  workspaceId: string;
  editType: EditType;
  mode: string;
  onHideEdit: () => void;
  openCreate: () => void;
}


export const formatTapdInfo = (tapdInfo: IBasicItemInfo[], customFields: CustomFieldsType[]) => {
  const newFields = tapdInfo.map((item: IBasicItemInfo) => {
    const findcustom = customFields.find((custom: CustomFieldsType) => custom.name === item.name);
    return ({
      name: item.name,
      value: item.value,
      label: item.label,
      type: findcustom?.type ?? '',
      options: findcustom?.options,
    });
  }).filter(item => item.name !== 'title_prefix');
  const newTitlePrefix = tapdInfo.find((item: IBasicItemInfo) => item.name === 'title_prefix')?.value;
  return {
    newFields,
    newTitlePrefix,
  };
};

export const EditTapdModal: FC<IProps> = (props) => {
  const { visible, workspaceId, editType, onHideEdit, openCreate } = props;
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState<FieldsType[]>([]);
  const [relateStatus, setRelateStatus] = useState<IStatusLinkageRule[]>([]);
  const [customFields, setCustomFields] = useState<FieldsType[]>([]);
  const [statusFields, setStatusFields] = useState<FieldsType>();
  const [tapdTitlePrefix, setTapdTitlePrefix] = useState('');
  const [mode, setMode] = useState<string>('');
  const [tapdTemplateInfos, setTapdTemplateInfos] = useState<ITapdTemplateInfos[]>([]);
  const initTitlePrefix = '【Bugly-质量监控】';

  const saveTapdInfo = async () => {
    if (!mode) {
      message.warning('请输入模板名称');
      return;
    }
    const isIncomplete = relateStatus.some(item => item.thenIssue === '');
    if (isIncomplete) {
      message.warning('状态联动未填写完整');
      return;
    }
    setLoading(true);
    const modeId = await saveIssueTapdInfos({
      productId: getProductId(),
      workSpaceId: workspaceId,
      operator: getUserId(),
      name: mode,
      issueTapdTemplateId: editType === 'update'
        ? tapdTemplateInfos.find(item => item.templateId === props.mode)?.templateId
        : undefined,
      data: [
        ...formatFieldsValue(fields),
        {
          name: 'title_prefix',
          value: tapdTitlePrefix,
          label: '标题前缀',
        },
      ],
      statusRules: relateStatus,
    });
    storageSet(`${getProductId()}-tapd`, JSON.stringify({ ...getLocalTapd(), mode: modeId ?? getLocalTapd()?.mode }));
    setLoading(false);
  };

  const loadTapdFields = async () => {
    setLoading(true);
    const customFields = await getCustomFields(workspaceId);
    const newCustomFields = formatCustomFields(customFields, props.appVersionOptions);
    const tapdTemplateInfos = await getIssueTapdInfos({
      productId: getProductId(),
      workSpaceId: workspaceId,
    });
    setTapdTemplateInfos(tapdTemplateInfos);
    const tapdInfo = tapdTemplateInfos.find(item => item.templateId === props.mode) ?? tapdTemplateInfos[0];
    editType === 'update' ? setMode(tapdInfo?.templateName ?? '') : setMode('');
    if (tapdInfo?.statusRules && editType === 'update') {
      setRelateStatus(tapdInfo.statusRules);
    } else {
      setRelateStatus([]);
    }
    const info = formatTapdInfo(tapdInfo?.data ?? [], newCustomFields);
    if (info.newTitlePrefix && editType === 'update') {
      setTapdTitlePrefix(info.newTitlePrefix);
    } else {
      setTapdTitlePrefix(initTitlePrefix);
    }
    if (!!newCustomFields.length) {
      setCustomFields(newCustomFields);
      const statusFields = newCustomFields.filter((item: CustomFieldsType) => item.name === 'status');
      setStatusFields(statusFields?.[0]);
    }
    if (!!tapdInfo?.data?.length && editType === 'update') {
      setFields(info.newFields);
    } else {
      setFields(getInitFields(props.appVersionOptions as FilterOptions[]));
    }
    setLoading(false);
  };

  const getNewFields = (changeValue: string | number, name?: string | null) => {
    const newFields = fields.map((item: FieldsType) => {
      if (item.name === name) {
        return { ...item, value: changeValue };
      }
      return item;
    });
    setFields(newFields);
  };

  const getNewStatus = (changeValue: string, name?: string | null) => {
    const newStatus = relateStatus.map((item) => {
      if (item.whenTapd === name) {
        return { ...item, thenIssue: changeValue };
      }
      return item;
    });
    setRelateStatus(newStatus);
  };

  const renderFields = (fieldsItem: FieldsType, index: number) => {
    const options = customFields?.map((item: CustomFieldsType) => ({ label: item.label, value: item.label }));
    const renderItem = () => {
      switch (fieldsItem.type) {
        case 'select' :
          return <RenderSelect
            name={fieldsItem.name}
            label={'默认值'}
            options={fieldsItem.options!}
            getNewFields={getNewFields}
            value={fieldsItem.value}
            labelWidth={'60px'}
          />;
        case 'autoComplete':
          return <RenderAutoComplete
            name={fieldsItem.name}
            label={'默认值'}
            options={fieldsItem.options!}
            getNewFields={getNewFields}
            value={fieldsItem.value}
            labelWidth={'60px'}
          />;
        case 'userSelect':
          return <RenderUserSelect
            name={fieldsItem.name}
            label={'默认值'}
            getNewFields={getNewFields}
            value={fieldsItem.value as string}
            labelWidth={'60px'}
          />;
        default:
          return <RenderInput
            name={fieldsItem.name}
            label={'默认值'}
            value={fieldsItem.value}
            getNewFields={getNewFields}
            labelWidth={'60px'}
          />;
      }
    };
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ marginBottom: '10px', marginRight: '12px', display: 'flex' }}>
          <span style={{ lineHeight: '30px', marginRight: '12px', textAlign: 'right', width: '58px' }}>
            字段名
          </span>
          <Select
            placeholder={'请选择'}
            options={options}
            showSearch
            value={fieldsItem.label}
            onChange={(_, changeValue: any) => {
              const repeatFields = fields.find((item: FieldsType) => item.label === changeValue.label);
              if (repeatFields) {
                message.warning('字段名重复，请重新选择');
                setFields(fields);
                return;
              }
              const filterFields = fields.filter(item => item.name !== fieldsItem.name);
              const newFields = customFields.find((item: CustomFieldsType) => item.label === changeValue.label);
              setFields([...filterFields, newFields!]);
            }}
            style={{ width: '200px' }}
          />
        </div>
        {renderItem()}
        <img
          src={TapdFieldDelete}
          onClick={() => {
            const newFields = fields?.filter((_item: FieldsType, itemIndex: number) => itemIndex !== index);
            setFields(newFields);
          }}
          style={{
            height: '18px',
            marginTop: '6px',
            marginLeft: '10px',
            cursor: 'pointer',
          }}
          alt={''}
        />
      </div>
    );
  };

  const renderRelateStatus = (fieldsItem: IStatusLinkageRule, index: number) => (
      <div style={{ display: 'flex', marginBottom: '10px', marginLeft: '16px' }}>
        <div style={{ marginBottom: '10px' }}>
          <span style={{ lineHeight: '30px' }}>当TAPD状态为</span>
          <Select
            placeholder={'请选择'}
            style={{ width: '140px', margin: '0 5px' }}
            options={statusFields?.options || []}
            value={fieldsItem.whenTapd}
            onChange={(_, changeValue: any) => {
              const repeatFields = relateStatus.find(item => item.whenTapd === changeValue.value);
              if (repeatFields) {
                message.warning('状态名重复，请重新选择');
                setRelateStatus(relateStatus);
                return;
              }
              const filterFields = relateStatus.filter(item => (
                item.whenTapd !== fieldsItem.whenTapd
              ));
              setRelateStatus([...filterFields, { whenTapd: changeValue.value, thenIssue: '' }]);
            }}
          />
          <span style={{ lineHeight: '30px' }}>时，Issue状态更改为</span>
          <Select
            placeholder={'请选择'}
            style={{ width: '120px', margin: '0 5px' }}
            options={getLocalField(FieldKey.IssueLinkedStatus)}
            value={fieldsItem.thenIssue}
            onChange={(value: string) => getNewStatus(value, fieldsItem.whenTapd)}
          />
        </div>
        <img
          src={TapdFieldDelete}
          onClick={() => {
            const newRelateStatus = relateStatus?.filter((_item: any, itemIndex: number) => itemIndex !== index);
            setRelateStatus(newRelateStatus);
          }}
          style={{
            height: '18px',
            marginTop: '6px',
            marginLeft: '10px',
            cursor: 'pointer',
          }}
          alt={''}
        />
      </div>
  );

  useEffect(() => {
    if (visible) loadTapdFields();
  }, [visible]);

  return (
    <Modal
      title={<div style={{
        fontFamily: 'PingFang SC',
        fontWeight: 500,
        fontSize: '16px',
      }}>{editType === 'add' ? '创建新模板' : '编辑模板'}</div>}
      centered
      visible={visible}
      footer={null}
      onCancel={() => {
        setFields(fields);
        onHideEdit();
        openCreate();
      }}
      style={{ minWidth: '640px' }}
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', marginBottom: '15px' }}>
          <span style={{ minWidth: '70px', lineHeight: '30px', marginRight: '1px' }}>模板名称</span>
          <Input
            placeholder={'请输入模板名称'}
            onChange={e => setMode(e.target.value)}
            defaultValue={mode}
            value={mode}
          />
        </div>
        <div style={{ display: 'flex' }}>
          <span style={{ minWidth: '70px', lineHeight: '30px', marginRight: '1px' }}>标题前缀</span>
          <Input
            value={tapdTitlePrefix}
            onChange={(e) => {
              setTapdTitlePrefix(e.target.value);
            }}
          />
        </div>
        <div style={{ marginBottom: '15px', fontSize: '16px', marginTop: '30px', fontWeight: 500, fontFamily: 'PingFang SC' }}>
          基本信息
        </div>
        {fields.map((item, index) => renderFields(item, index))}
        <div
          style={{
            marginLeft: '16px',
            color: '#0A6CFF',
            cursor: 'pointer',
            width: '80px',
          }}
        >
          <div onClick={() => {
            const filterFieldsList = customFields
              ?.filter((item: CustomFieldsType) => !fields.some(fields => fields.name === item.name));
            if (!filterFieldsList.length) {
              message.warning('该tapd项目下的字段名已全部添加');
              return;
            }
            setFields([...fields, filterFieldsList[0] ?? {}]);
          }}>
            <PlusCircleOutlined style={{ marginRight: '8px' }}/>
            添加字段
          </div>
        </div>
        <div style={{
          marginBottom: '20px',
          fontSize: '14px',
          marginTop: '40px',
          fontWeight: 500,
          fontFamily: 'PingFang SC',
          // 联动规则不显示，功能暂时不下线
          display: 'none',
        }}>
          Issue处理状态联动
        </div>
        {relateStatus.map((item, index) => renderRelateStatus(item, index))}
        <div
          style={{
            marginLeft: '16px',
            color: '#0A6CFF',
            cursor: 'pointer',
            width: '110px',
            // 联动规则不显示，功能暂时不下线
            display: 'none',
          }}
        >
          <div onClick={() => {
            if (statusFields?.options && statusFields.options?.length <= relateStatus.length) {
              message.warning('该tapd项目下的状态类型已全部添加');
              return;
            }
            const newStatus = statusFields?.options?.find(option => (
              relateStatus.every(item => item.whenTapd !== option.value)
            ));
            setRelateStatus([...relateStatus, { whenTapd: typeof(newStatus?.value) === 'number' ? newStatus?.value.toString() : newStatus?.value, thenIssue: '' }]);
          }}>
            <PlusCircleOutlined style={{ marginRight: '8px' }} />
            添加联动规则
          </div>
        </div>
        <div style={{ textAlign: 'right', marginTop: '40px' }}>
          <Button onClick={() => {
            onHideEdit();
            openCreate();
          }}>
            取消
          </Button>
          <Button
            type='primary'
            onClick={async () => {
              await saveTapdInfo();
              mode && onHideEdit();
              mode && openCreate();
            }}
            style={{ marginLeft: '20px' }}>
            确定
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};
